import React, { useEffect, useMemo, useState } from 'react';
import { DemeterSymbolModel, SymbolCategory } from '../../../../Generated/Raven-Demeter';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { EventDataTargetsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import { ITreeNode } from '../../TreeView/TreeItem';
import styles from './ProductsModal.module.scss';
import ProductsModalWrapper from './ProductsModalWrapper';
import SelectProduct from './SelectProduct';

interface ICalendarSpreadCalculatorModalProps {
    productOptions: DemeterSymbolModel[];
    open: boolean;
    symbol1: DemeterSymbolModel;
    symbol2: DemeterSymbolModel;
    handleChange: (symbol1: DemeterSymbolModel, symbol2: DemeterSymbolModel) => void;
    handleClose: () => void;
}

const CalendarSpreadCalculatorModal: React.FC<ICalendarSpreadCalculatorModalProps> = (props) => {
    const [translations, translate] = useLanguage();
    const [productState, setProductState] = useState({
        product1Node: null as ITreeNode | null | undefined,
        product2Node: null as ITreeNode | null | undefined,
        productPairProduct1Id: props.symbol1.reutersInstrumentCodePrefix,
        productPairProduct2Id: props.symbol2.reutersInstrumentCodePrefix,
    });
    const [treeNodes, setTreeNodes] = useState<ITreeNode[]>([]);
    const isSetButtonDisabled = !productState.product1Node || !productState.product2Node;

    const setProductPair = () => {
        if (!productState.product1Node || !productState.product2Node) {
            return;
        }

        const selectedProduct1 = props.productOptions.find((x) => productState.product1Node && x.reutersInstrumentCodePrefix === productState.product1Node.id);
        const selectedProduct2 = props.productOptions.find((x) => productState.product2Node && x.reutersInstrumentCodePrefix === productState.product2Node.id);

        if (!selectedProduct1 || !selectedProduct2) {
            return;
        }

        props.handleChange(selectedProduct1, selectedProduct2);
        props.handleClose();
    };

    const handleProduct1Selection = (node: ITreeNode) => {
        setProductState((previous) => ({ ...previous, product1Node: node, productPairProduct1Id: node.id }));
    };

    const handleProduct2Selection = (node: ITreeNode) => {
        setProductState((previous) => ({ ...previous, product2Node: node, productPairProduct2Id: node.id }));
    };

    const buildValuePath = (product: DemeterSymbolModel) => {
        const market = translations.market[product.symbolCategory] ?? product.symbolCategory;
        const exchange = translations.exchange[product.exchange] ?? product.exchange;
        const name = translate(product.name);
        return `root_${market}_${exchange}_${name}`;
    };

    const mapToTreeNodes = (products: DemeterSymbolModel[] | undefined): ITreeNode[] => {
        if (!products) {
            return [];
        }

        return products.map((product, index) => {
            const name = translate(product.name);
            const parentGrouping = product.exchange;
            const item: ITreeNode = {
                id: product.reutersInstrumentCodePrefix,
                value: buildValuePath(product),
                name,
                parentValue: parentGrouping,
                parentId: parentGrouping,
                order: index,
                displayOrder: -1,
                expanded: false,
                selectedProduct: false,
                children: [],
            };

            return item;
        });
    };

    useEffect(() => {
        setTreeNodes(mapToTreeNodes(props.productOptions));
    }, [props.productOptions]);

    useEffect(() => {
        if (props.open && treeNodes.length > 0) {
            const currentProduct1Node = treeNodes.find((node) => node.id === props.symbol1.reutersInstrumentCodePrefix);
            if (currentProduct1Node && !productState.product1Node) {
                setProductState((previous) => ({ ...previous, product1Node: currentProduct1Node }));
            }

            const currentProduct2Node = treeNodes.find((node) => node.id === props.symbol2.reutersInstrumentCodePrefix);
            if (currentProduct2Node && !productState.product2Node) {
                setProductState((previous) => ({ ...previous, product2Node: currentProduct2Node }));
            }
        }
    }, [
        props.open,
        props.symbol1.reutersInstrumentCodePrefix,
        props.symbol2.reutersInstrumentCodePrefix,
        treeNodes,
        productState.product1Node,
        productState.product2Node,
    ]);

    const disabledSymbolCatagories = useMemo(() => {
        if (!productState.product1Node) {
            return [];
        }

        const product1SymbolCategory = props.productOptions.find(
            (x) => productState.product1Node && x.reutersInstrumentCodePrefix === productState.product1Node.id,
        )?.symbolCategory;

        if (!product1SymbolCategory) {
            return [];
        }

        const possibleSymbolCatagories = Array.from(
            new Set([
                ...props.productOptions.map((product) => product.symbolCategory),
                ...props.productOptions.map((product) => translate(product.symbolCategory)),
            ]),
        );

        if (product1SymbolCategory === SymbolCategory.Energy) {
            return possibleSymbolCatagories.filter((catagory) => catagory !== SymbolCategory.Energy);
        }

        return possibleSymbolCatagories.filter((catagory) => catagory === SymbolCategory.Energy);
    }, [productState.product1Node]);

    useEffect(() => {
        if (!productState.product2Node || !productState.product1Node) {
            return;
        }
        const product2SymbolCategory = props.productOptions.find((x) => x.reutersInstrumentCodePrefix === productState.product2Node!.id)?.symbolCategory;
        if (disabledSymbolCatagories.some((x) => x === product2SymbolCategory)) {
            handleProduct2Selection(productState.product1Node);
            setProductState((previous) => ({
                ...previous,
                productPairProduct2Id: productState.product1Node!.id,
                productPairProduct1Id: productState.product1Node!.id,
            }));
            setTreeNodes(mapToTreeNodes(props.productOptions));
        }
    }, [disabledSymbolCatagories]);

    return (
        <div>
            <ProductsModalWrapper
                name="CalendarSpreadCalculatorModal"
                dataTarget={EventDataTargetsEnum.SelectModal}
                title={translations.calculators.text.calendarSpreadCalculator}
                open={props.open}
                isSetButtonDisabled={isSetButtonDisabled}
                handleClose={props.handleClose}
                handleSave={setProductPair}
                track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'ModalSelectProduct' }}
            >
                <div className={styles.products_modal_content}>
                    <div className={styles.products_modal_body}>
                        <div className={styles.products_modal_tree_selection_row}>
                            <SelectProduct
                                title={translations.calculators.basis.productSelector.tabs.productsTab.product1}
                                productTreeNodes={treeNodes}
                                expandedDepth={0}
                                productPairProductId={productState.productPairProduct1Id}
                                enableUserData={false}
                                setProduct={handleProduct1Selection}
                                track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'ModalSelectProduct1' }}
                            />
                            <SelectProduct
                                title={translations.calculators.basis.productSelector.tabs.productsTab.product2}
                                productTreeNodes={treeNodes}
                                expandedDepth={0}
                                productPairProductId={productState.productPairProduct2Id}
                                disabledNodesById={disabledSymbolCatagories}
                                enableUserData={false}
                                setProduct={handleProduct2Selection}
                                track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'ModalSelectProduct2' }}
                            />
                        </div>
                    </div>
                </div>
            </ProductsModalWrapper>
        </div>
    );
};

export default CalendarSpreadCalculatorModal;
